import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Mountain, ArrowLeft, Home, MapPin, IndianRupee, Users, Bed, Bath,
  Clock, Wifi, Car, Flame, Tv, Wind, UtensilsCrossed, Trees, Camera,
  Plus, X, Loader2, Upload, Instagram, Building, Image, Check, Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const propertyTypes = ['Homestay', 'Cottage', 'Villa', 'Resort', 'Cabin', 'Farmstay', 'Treehouse', 'Tent', 'Houseboat', 'Other'];
const amenitiesList = ['WiFi', 'Parking', 'Kitchen', 'Hot Water', 'Heating', 'TV', 'Washing Machine', 'Balcony', 'Garden', 'Bonfire', 'BBQ', 'Mountain View', 'River View', 'Pet Friendly'];

const CreateStayListing = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [images, setImages] = useState([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importType, setImportType] = useState(null);
  const [instagramHandle, setInstagramHandle] = useState('');
  const [googleSearchQuery, setGoogleSearchQuery] = useState('');
  const [googleResults, setGoogleResults] = useState([]);
  const [searchingGoogle, setSearchingGoogle] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    property_type: '',
    location: {
      address: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India'
    },
    price_per_night: '',
    max_guests: '',
    bedrooms: '1',
    bathrooms: '1',
    amenities: [],
    house_rules: [],
    check_in_time: '14:00',
    check_out_time: '11:00'
  });

  const [newRule, setNewRule] = useState('');

  const getAuthHeaders = () => {
    const token = localStorage.getItem('supplier_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  const toggleAmenity = (amenity) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };

  const addRule = () => {
    if (newRule.trim()) {
      setFormData(prev => ({
        ...prev,
        house_rules: [...prev.house_rules, newRule.trim()]
      }));
      setNewRule('');
    }
  };

  const removeRule = (index) => {
    setFormData(prev => ({
      ...prev,
      house_rules: prev.house_rules.filter((_, i) => i !== index)
    }));
  };

  // Handle file upload
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingImages(true);
    const token = localStorage.getItem('supplier_token');
    
    const formDataUpload = new FormData();
    files.forEach(file => formDataUpload.append('files', file));

    try {
      const response = await fetch(`${API_URL}/api/supplier/upload/images`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataUpload
      });

      if (response.ok) {
        const data = await response.json();
        setImages(prev => [...prev, ...data.urls.map(url => `${API_URL}${url}`)]);
        toast.success(`${data.count} image(s) uploaded!`);
      } else {
        toast.error('Failed to upload images');
      }
    } catch (error) {
      toast.error('Upload failed. Please try again.');
    }
    setUploadingImages(false);
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  // Google Maps search
  const searchGooglePlaces = async () => {
    if (!googleSearchQuery.trim()) return;
    setSearchingGoogle(true);

    try {
      const response = await fetch(`${API_URL}/api/supplier/google-places/search?query=${encodeURIComponent(googleSearchQuery)}`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setGoogleResults(data.results || []);
      }
    } catch (error) {
      toast.error('Search failed');
    }
    setSearchingGoogle(false);
  };

  const importFromGoogle = async (placeId) => {
    try {
      const response = await fetch(`${API_URL}/api/supplier/google-places/import`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ place_id: placeId })
      });

      if (response.ok) {
        const data = await response.json();
        // Auto-fill form with imported data
        setFormData(prev => ({
          ...prev,
          title: data.name || prev.title,
          description: data.description || prev.description,
          location: {
            ...prev.location,
            address: data.address || prev.location.address,
            city: data.city || prev.location.city,
            state: data.state || prev.location.state
          }
        }));
        // Add photos
        if (data.photos) {
          setImages(prev => [...prev, ...data.photos]);
        }
        toast.success('Business details imported!');
        setShowImportModal(false);
      }
    } catch (error) {
      toast.error('Import failed');
    }
  };

  // Instagram import (mock for now)
  const importFromInstagram = async () => {
    if (!instagramHandle.trim()) return;
    
    // Mock import - in real app, would use Instagram API
    toast.success(`Connected to @${instagramHandle}. Photos will be imported.`);
    setShowImportModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.description || !formData.property_type) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (!formData.location.city || !formData.location.state) {
      toast.error('Please enter location details');
      return;
    }
    if (!formData.price_per_night || !formData.max_guests) {
      toast.error('Please enter pricing and guest capacity');
      return;
    }
    if (images.length === 0) {
      toast.error('Please add at least one photo');
      return;
    }

    setLoading(true);
    
    try {
      const payload = {
        ...formData,
        images: images,
        videos: [],
        price_per_night: parseFloat(formData.price_per_night),
        max_guests: parseInt(formData.max_guests),
        bedrooms: parseInt(formData.bedrooms) || 1,
        bathrooms: parseInt(formData.bathrooms) || 1
      };

      const response = await fetch(`${API_URL}/api/supplier/listings/stays`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Listing created successfully!');
        navigate('/supplier/dashboard');
      } else {
        toast.error(data.detail || 'Failed to create listing');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button onClick={() => navigate('/supplier/dashboard')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
              <ArrowLeft className="w-5 h-5" />
              Back to Dashboard
            </button>
            <Link to="/" className="flex items-center gap-2">
              <Mountain className="h-7 w-7 text-[#2C5F8D]" />
              <span className="text-lg font-bold text-[#2C5F8D]" style={{fontFamily: 'Playfair Display, serif'}}>GoHimalayan</span>
            </Link>
          </div>
        </div>
      </header>

      <form onSubmit={handleSubmit} className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900" style={{fontFamily: 'Playfair Display, serif'}}>Add Your Property</h1>
          <p className="text-gray-500 mt-2">List your unique stay on GoHimalayan</p>
        </div>

        {/* Photo Upload Section */}
        <div className="bg-white rounded-2xl p-6 shadow-sm mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <Camera className="w-5 h-5 text-[#2C5F8D]" />
              Photos
            </h2>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => { setImportType('google'); setShowImportModal(true); }}
              >
                <Building className="w-4 h-4 mr-2" />
                Import from Google
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => { setImportType('instagram'); setShowImportModal(true); }}
              >
                <Instagram className="w-4 h-4 mr-2" />
                Import from Instagram
              </Button>
            </div>
          </div>

          {/* Image Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            {images.map((url, index) => (
              <div key={index} className="relative aspect-square rounded-xl overflow-hidden group">
                <img src={url} alt={`Photo ${index + 1}`} className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-4 h-4" />
                </button>
                {index === 0 && (
                  <span className="absolute bottom-2 left-2 px-2 py-1 bg-black/70 text-white text-xs rounded">
                    Cover Photo
                  </span>
                )}
              </div>
            ))}

            {/* Upload Button */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingImages}
              className="aspect-square rounded-xl border-2 border-dashed border-gray-300 hover:border-[#2C5F8D] flex flex-col items-center justify-center gap-2 text-gray-500 hover:text-[#2C5F8D] transition-colors"
            >
              {uploadingImages ? (
                <Loader2 className="w-8 h-8 animate-spin" />
              ) : (
                <>
                  <Upload className="w-8 h-8" />
                  <span className="text-sm font-medium">Add Photos</span>
                </>
              )}
            </button>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileUpload}
            className="hidden"
          />

          <p className="text-sm text-gray-500">
            Add at least 5 photos. First photo will be your cover image. Max 5MB per image.
          </p>
        </div>

        {/* Basic Information */}
        <div className="bg-white rounded-2xl p-6 shadow-sm mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Home className="w-5 h-5 text-[#2C5F8D]" />
            Basic Information
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Property Title *</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g., Cozy Mountain Cottage with Valley Views"
                className="h-12"
                data-testid="title-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe your property, its unique features, and what makes it special..."
                className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#2C5F8D] focus:outline-none min-h-[120px] resize-none"
                data-testid="description-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Property Type *</label>
              <div className="flex flex-wrap gap-2">
                {propertyTypes.map(type => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, property_type: type.toLowerCase() }))}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      formData.property_type === type.toLowerCase()
                        ? 'bg-[#2C5F8D] text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Location */}
        <div className="bg-white rounded-2xl p-6 shadow-sm mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-[#2C5F8D]" />
            Location
          </h2>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
              <Input
                value={formData.location.address}
                onChange={(e) => setFormData(prev => ({ ...prev, location: { ...prev.location, address: e.target.value } }))}
                placeholder="Street address"
                className="h-12"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">City *</label>
              <Input
                value={formData.location.city}
                onChange={(e) => setFormData(prev => ({ ...prev, location: { ...prev.location, city: e.target.value } }))}
                placeholder="e.g., Manali"
                className="h-12"
                name="location.city"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">State *</label>
              <Input
                value={formData.location.state}
                onChange={(e) => setFormData(prev => ({ ...prev, location: { ...prev.location, state: e.target.value } }))}
                placeholder="e.g., Himachal Pradesh"
                className="h-12"
                name="location.state"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Pincode</label>
              <Input
                value={formData.location.pincode}
                onChange={(e) => setFormData(prev => ({ ...prev, location: { ...prev.location, pincode: e.target.value } }))}
                placeholder="e.g., 175131"
                className="h-12"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
              <Input value="India" disabled className="h-12 bg-gray-50" />
            </div>
          </div>
        </div>

        {/* Pricing & Capacity */}
        <div className="bg-white rounded-2xl p-6 shadow-sm mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <IndianRupee className="w-5 h-5 text-[#2C5F8D]" />
            Pricing & Capacity
          </h2>

          <div className="grid md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Price per Night (₹) *</label>
              <Input
                type="number"
                value={formData.price_per_night}
                onChange={(e) => setFormData(prev => ({ ...prev, price_per_night: e.target.value }))}
                placeholder="3500"
                className="h-12"
                name="price_per_night"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Max Guests *</label>
              <Input
                type="number"
                value={formData.max_guests}
                onChange={(e) => setFormData(prev => ({ ...prev, max_guests: e.target.value }))}
                placeholder="6"
                className="h-12"
                name="max_guests"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Bedrooms</label>
              <Input
                type="number"
                value={formData.bedrooms}
                onChange={(e) => setFormData(prev => ({ ...prev, bedrooms: e.target.value }))}
                placeholder="3"
                className="h-12"
                name="bedrooms"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Bathrooms</label>
              <Input
                type="number"
                value={formData.bathrooms}
                onChange={(e) => setFormData(prev => ({ ...prev, bathrooms: e.target.value }))}
                placeholder="2"
                className="h-12"
                name="bathrooms"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Check-in Time</label>
              <Input
                type="time"
                value={formData.check_in_time}
                onChange={(e) => setFormData(prev => ({ ...prev, check_in_time: e.target.value }))}
                className="h-12"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Check-out Time</label>
              <Input
                type="time"
                value={formData.check_out_time}
                onChange={(e) => setFormData(prev => ({ ...prev, check_out_time: e.target.value }))}
                className="h-12"
              />
            </div>
          </div>
        </div>

        {/* Amenities */}
        <div className="bg-white rounded-2xl p-6 shadow-sm mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Amenities</h2>
          <div className="flex flex-wrap gap-2">
            {amenitiesList.map(amenity => (
              <button
                key={amenity}
                type="button"
                onClick={() => toggleAmenity(amenity)}
                className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 transition-all ${
                  formData.amenities.includes(amenity)
                    ? 'bg-[#2C5F8D] text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {formData.amenities.includes(amenity) && <Check className="w-4 h-4" />}
                {amenity}
              </button>
            ))}
          </div>
        </div>

        {/* House Rules */}
        <div className="bg-white rounded-2xl p-6 shadow-sm mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">House Rules</h2>
          
          <div className="flex gap-2 mb-4">
            <Input
              value={newRule}
              onChange={(e) => setNewRule(e.target.value)}
              placeholder="Add a house rule..."
              className="h-12"
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addRule())}
            />
            <Button type="button" onClick={addRule} variant="outline">
              <Plus className="w-4 h-4" />
            </Button>
          </div>

          <div className="space-y-2">
            {formData.house_rules.map((rule, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-700">{rule}</span>
                <button type="button" onClick={() => removeRule(index)} className="text-gray-400 hover:text-red-500">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Submit */}
        <div className="flex gap-4">
          <Button type="button" variant="outline" onClick={() => navigate('/supplier/dashboard')} className="flex-1 h-14">
            Cancel
          </Button>
          <Button type="submit" disabled={loading} className="flex-1 h-14 bg-[#2C5F8D] hover:bg-[#1e4570] text-lg" data-testid="submit-listing-btn">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Create Listing'}
          </Button>
        </div>
      </form>

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">
                {importType === 'google' ? 'Import from Google Maps' : 'Import from Instagram'}
              </h2>
              <button onClick={() => setShowImportModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              {importType === 'google' ? (
                <>
                  <p className="text-gray-600 mb-4">Search for your business to import photos and details.</p>
                  <div className="flex gap-2 mb-4">
                    <Input
                      value={googleSearchQuery}
                      onChange={(e) => setGoogleSearchQuery(e.target.value)}
                      placeholder="Search your business name..."
                      className="flex-1"
                      onKeyPress={(e) => e.key === 'Enter' && searchGooglePlaces()}
                    />
                    <Button onClick={searchGooglePlaces} disabled={searchingGoogle}>
                      {searchingGoogle ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
                    </Button>
                  </div>

                  <div className="space-y-3">
                    {googleResults.map((place) => (
                      <div
                        key={place.place_id}
                        onClick={() => importFromGoogle(place.place_id)}
                        className="flex items-center gap-3 p-4 border rounded-xl hover:bg-gray-50 cursor-pointer"
                      >
                        <div className="w-16 h-16 rounded-lg bg-gray-100 overflow-hidden">
                          {place.photos?.[0] ? (
                            <img src={place.photos[0]} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center"><Building className="w-6 h-6 text-gray-300" /></div>
                          )}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{place.name}</h4>
                          <p className="text-sm text-gray-500">{place.address}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <p className="text-gray-600 mb-4">Connect your Instagram to import photos.</p>
                  <div className="relative mb-4">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">@</span>
                    <Input
                      value={instagramHandle}
                      onChange={(e) => setInstagramHandle(e.target.value.replace('@', ''))}
                      placeholder="your_business"
                      className="pl-8"
                    />
                  </div>
                  <Button onClick={importFromInstagram} className="w-full bg-gradient-to-r from-purple-500 to-pink-500">
                    <Instagram className="w-4 h-4 mr-2" />
                    Connect & Import Photos
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateStayListing;
